import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { NotificationTemplate } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { isCustomised } from '../schemas/placeholder-rules';

const FAMILIES = ['order', 'draft_order', 'return', 'checkout'] as const;

export interface TemplatesListProps {
  templates: readonly NotificationTemplate[];
  canWrite: boolean;
  busyEvent: string | null;
  onToggle: (template: NotificationTemplate) => void;
  onEdit: (template: NotificationTemplate) => void;
}

/** Emails grouped by what triggers them, each with its on/off switch and an editor. */
export function TemplatesList({
  templates,
  canWrite,
  busyEvent,
  onToggle,
  onEdit,
}: TemplatesListProps) {
  const { t } = useTranslation('notifications');
  const firstFamily = FAMILIES.find((family) =>
    templates.some((row) => row.event.startsWith(`${family}.`)),
  );
  const firstEvent = templates.find((row) => row.event.startsWith(`${firstFamily}.`))?.event;

  return (
    <div className="flex flex-col gap-6">
      {FAMILIES.map((family) => {
        const rows = templates.filter((row) => row.event.startsWith(`${family}.`));
        if (rows.length === 0) return null;
        return (
          <Card
            key={family}
            className="gap-2"
            data-tour={family === firstFamily ? 'settings.notifications.family' : undefined}
          >
            <CardHeader>
              <CardTitle>{t(`family.${family}`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y" aria-label={t(`family.${family}`)}>
                {rows.map((template) => {
                  const title = t(`event.${template.event}`);
                  return (
                    <li
                      key={template.event}
                      aria-label={title}
                      className={cn(
                        'flex flex-wrap items-center justify-between gap-3 py-3',
                        !template.isEnabled && 'text-muted-foreground',
                      )}
                    >
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-foreground font-medium">{title}</span>
                          <code className="text-muted-foreground text-xs">{template.event}</code>
                          {isCustomised(template) ? (
                            <Badge variant="secondary">{t('customised')}</Badge>
                          ) : null}
                          {template.isEnabled ? null : <Badge variant="outline">{t('off')}</Badge>}
                        </span>
                        <span className="text-sm">{template.description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {canWrite ? (
                          <Switch
                            checked={template.isEnabled}
                            disabled={busyEvent === template.event}
                            aria-label={t('toggle', { title })}
                            onCheckedChange={() => onToggle(template)}
                            data-tour={
                              template.event === firstEvent
                                ? 'settings.notifications.toggle'
                                : undefined
                            }
                          />
                        ) : null}
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label={t(canWrite ? 'edit' : 'view', { title })}
                          onClick={() => onEdit(template)}
                          data-tour={
                            template.event === firstEvent
                              ? 'settings.notifications.edit'
                              : undefined
                          }
                        >
                          <Pencil aria-hidden />
                          {canWrite ? t('editShort') : t('viewShort')}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
